import React, { useEffect, useMemo, useState } from 'react';
import { ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { RotateCcw } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { money, moneyK, mesLabel, Title, Empty, Kpi } from './ui';

const tooltipStyle = { background: 'var(--tooltip-bg)', border: '1px solid var(--border)', borderRadius: 8, fontFamily: 'Raleway', color: 'var(--text-primary)' };
const tick = { fill: 'var(--chart-text)', fontSize: 11 };
const th = { textAlign: 'left', fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, padding: '6px 8px' };
const td = { padding: '8px', borderTop: '1px solid var(--border)', fontSize: 13 };

const BASE = { horizonte: 12, aumento: 0, desdeMes: 2, bajas: 0, cierres: null, egresos: 0, cobrabilidad: 90 };

// Cada escenario es un ajuste sobre los números reales; nada se inventa de cero
function Control({ label, valor, onChange, min, max, paso = 1, sufijo = '%', ayuda }) {
  return (
    <div style={{ minWidth: 190, flex: '1 1 190px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-secondary)', fontWeight: 600 }}>
        <span>{label}</span><span style={{ color: 'var(--accent)' }}>{valor > 0 && sufijo === '%' ? '+' : ''}{valor}{sufijo}</span>
      </div>
      <input type="range" min={min} max={max} step={paso} value={valor} onChange={e => onChange(Number(e.target.value))}
        style={{ width: '100%', padding: 0, marginTop: 6, accentColor: 'var(--accent)' }} />
      {ayuda && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{ayuda}</div>}
    </div>
  );
}

export default function AgenciaPlanificacion() {
  const [d, setD] = useState(null);
  const [e, setE] = useState(BASE);
  const [meta, setMeta] = useState('');

  useEffect(() => {
    api.get('/agency/proyeccion', { params: { meses: 24 } }).then(r => {
      setD(r.data);
      setE(x => ({ ...x, cierres: Math.round((r.data.supuestos.efectividad || 0.5) * 100) }));
    }).catch(() => toast.error('No se pudo cargar'));
  }, []);

  const serie = useMemo(() => {
    if (!d) return [];
    const s = d.supuestos;
    const porCerrar = s.propuestas_abiertas.total * (e.cierres ?? 50) / 100;
    const cobrable = e.cobrabilidad / 100;
    return d.meses.slice(0, e.horizonte).map((m, i) => {
      // los abonos cambian a partir del mes elegido, y se descuentan las bajas previstas
      const factor = (i + 1 >= e.desdeMes ? 1 + e.aumento / 100 : 1) * (1 - (e.bajas / 100) * Math.min(i / 6, 1));
      const abonos = Math.round(m.abonos * factor);
      // lo que está en juego se reparte entre los tres primeros meses
      const probable = i < 3 ? Math.round(porCerrar / 3) : 0;
      const atrasado = i === 0 ? Math.round((m.atrasado || 0) * cobrable) : 0;
      const pendiente = Math.round(m.pendiente * cobrable);
      const egresos = Math.round(m.egresos * (1 + e.egresos / 100));
      const ingresos = abonos + m.hosting + pendiente + m.a_pasar + probable + atrasado;
      return {
        mes: mesLabel(m.mes), mesRaw: m.mes,
        abonos, hosting: m.hosting, pendiente: pendiente + atrasado, a_pasar: m.a_pasar, probable,
        egresos, ingresos, resultado: ingresos - egresos,
      };
    });
  }, [d, e]);

  if (!d) return <div style={{ color: 'var(--text-muted)', padding: 40 }}>Cargando...</div>;

  const s = d.supuestos;
  const totalIngresos = serie.reduce((a, m) => a + m.ingresos, 0);
  const totalEgresos = serie.reduce((a, m) => a + m.egresos, 0);
  const promedio = serie.length ? Math.round(totalIngresos / serie.length) : 0;
  const flojos = serie.filter(m => m.resultado < 0);
  const metaNum = Number(String(meta).replace(/[^\d]/g, ''));
  const faltaParaMeta = metaNum ? metaNum - (serie[0] ? serie[0].ingresos : 0) : null;

  const Barra = ({ dataKey, fill, name }) => <Bar dataKey={dataKey} stackId="i" fill={fill} name={name} />;

  return (
    <div>
      <Title right={<button className="btn btn-secondary btn-sm" onClick={() => setE({ ...BASE, cierres: Math.round((s.efectividad || 0.5) * 100) })}>
        <RotateCcw size={14} /> Volver a los números reales</button>}>Planificación</Title>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
        <Kpi label={`Ingresos proyectados (${e.horizonte} meses)`} value={moneyK(totalIngresos)} color="var(--accent)" />
        <Kpi label="Promedio por mes" value={moneyK(promedio)} sub={`${s.abonos.cant} abonos de base`} />
        <Kpi label="Egresos estimados" value={moneyK(totalEgresos)} sub={`${moneyK(s.egreso_mensual)} por mes`} color="#ef4444" />
        <Kpi label="Resultado" value={moneyK(totalIngresos - totalEgresos)} color={totalIngresos > totalEgresos ? '#22c55e' : '#ef4444'}
          sub={flojos.length ? `${flojos.length} ${flojos.length === 1 ? 'mes en rojo' : 'meses en rojo'}` : 'todos los meses en positivo'} />
      </div>

      {/* Escenarios */}
      <div className="card" style={{ marginBottom: 20 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Escenarios</h3>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>
          Movés un control y el gráfico se recalcula. Nada de esto cambia tus datos: es para ver cómo quedaría.
        </div>
        <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap' }}>
          <Control label="Aumento de abonos" valor={e.aumento} onChange={v => setE({ ...e, aumento: v })} min={0} max={100}
            ayuda={e.aumento ? `desde el mes ${e.desdeMes}` : 'sin aumentos'} />
          <Control label="Aplicar desde el mes" valor={e.desdeMes} onChange={v => setE({ ...e, desdeMes: v })} min={1} max={12} sufijo="º" ayuda=" " />
          <Control label="Bajas de abonos" valor={e.bajas} onChange={v => setE({ ...e, bajas: v })} min={0} max={50}
            ayuda="se pierden de a poco en 6 meses" />
          <Control label="Propuestas que cierran" valor={e.cierres ?? 0} onChange={v => setE({ ...e, cierres: v })} min={0} max={100}
            ayuda={`${moneyK(s.propuestas_abiertas.total)} en juego · tu histórico: ${Math.round((s.efectividad || 0) * 100)}%`} />
          <Control label="Cobranza efectiva" valor={e.cobrabilidad} onChange={v => setE({ ...e, cobrabilidad: v })} min={50} max={100}
            ayuda="de lo pendiente y lo vencido" />
          <Control label="Variación de egresos" valor={e.egresos} onChange={v => setE({ ...e, egresos: v })} min={-50} max={100} ayuda=" " />
          <Control label="Horizonte" valor={e.horizonte} onChange={v => setE({ ...e, horizonte: v })} min={3} max={24} sufijo=" meses" ayuda=" " />
        </div>
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Cómo vienen los próximos meses</h3>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', margin: '8px 0 14px', fontSize: 12, color: 'var(--text-secondary)' }}>
          {[['Abonos', '#2E7DE2'], ['Hosting', '#14b8a6'], ['Ya facturado', '#f59e0b'], ['Cuotas a pasar', '#64748b'], ['Propuestas en juego', '#a855f7']].map(([l, c]) => (
            <span key={l} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 11, height: 11, borderRadius: 3, background: c }} />{l}</span>
          ))}
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 16, height: 3, background: '#ef4444' }} />Egresos</span>
        </div>
        {serie.length ? (
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart data={serie} margin={{ left: -6 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="mes" tick={tick} />
              <YAxis tick={tick} tickFormatter={moneyK} width={72} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [money(v), n]} />
              <Barra dataKey="abonos" fill="#2E7DE2" name="Abonos" />
              <Barra dataKey="hosting" fill="#14b8a6" name="Hosting" />
              <Barra dataKey="pendiente" fill="#f59e0b" name="Ya facturado" />
              <Barra dataKey="a_pasar" fill="#64748b" name="Cuotas a pasar" />
              <Barra dataKey="probable" fill="#a855f7" name="Propuestas en juego" />
              <Line type="monotone" dataKey="egresos" stroke="#ef4444" strokeWidth={2} dot={false} name="Egresos" />
            </ComposedChart>
          </ResponsiveContainer>
        ) : <Empty>Sin datos</Empty>}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(330px, 1fr))', gap: 20 }}>
        <div className="card">
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 10 }}>Mes a mes</h3>
          <div className="table-container">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={th}>Mes</th><th style={{ ...th, textAlign: 'right' }}>Ingresos</th>
                <th style={{ ...th, textAlign: 'right' }}>Egresos</th><th style={{ ...th, textAlign: 'right' }}>Resultado</th></tr></thead>
              <tbody>{serie.map(m => (
                <tr key={m.mesRaw}>
                  <td style={{ ...td, fontWeight: 600, textTransform: 'capitalize' }}>{m.mes}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{money(m.ingresos)}</td>
                  <td style={{ ...td, textAlign: 'right', color: 'var(--text-muted)' }}>{money(m.egresos)}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: m.resultado >= 0 ? '#22c55e' : '#ef4444' }}>{money(m.resultado)}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </div>

        <div className="card">
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>De dónde salen estos números</h3>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.9, marginTop: 10 }}>
            <div>· <b>{s.abonos.cant} abonos activos</b> por {money(s.abonos.total)} al mes, que se repiten todos los meses.</div>
            {s.pausados.cant > 0 && <div>· {s.pausados.cant} abonos en pausa por {money(s.pausados.total)}: no se cuentan, pero están ahí para retomar.</div>}
            <div>· Las <b>renovaciones de hosting</b> caen en el mes que vence cada una.</div>
            <div>· Lo <b>ya facturado y sin cobrar</b> ({money(s.atrasado)} vencido) entra según la cobranza efectiva que elijas.</div>
            <div>· Las <b>cuotas a pasar</b> de los proyectos, en el mes de su vencimiento.</div>
            <div>· <b>{moneyK(s.propuestas_abiertas.total)}</b> en {s.propuestas_abiertas.cant} propuestas abiertas, repartidas en los próximos tres meses.</div>
            <div>· Los <b>egresos</b> son el promedio de los últimos seis meses: {money(s.egreso_mensual)}.</div>
          </div>

          <h3 style={{ fontSize: 16, fontWeight: 700, margin: '20px 0 8px' }}>¿Llego a mi meta?</h3>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input value={meta} onChange={ev => setMeta(ev.target.value)} placeholder="Meta mensual"
              style={{ maxWidth: 180 }} inputMode="numeric" />
            {metaNum > 0 && (
              <span style={{ fontSize: 13, color: faltaParaMeta > 0 ? '#f59e0b' : '#22c55e', fontWeight: 600 }}>
                {faltaParaMeta > 0
                  ? `Te faltan ${money(faltaParaMeta)} el mes que viene`
                  : `Ya lo superás por ${money(Math.abs(faltaParaMeta))}`}
              </span>
            )}
          </div>
          {metaNum > 0 && faltaParaMeta > 0 && s.abonos.total > 0 && (
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8, lineHeight: 1.7 }}>
              Equivale a un aumento del {Math.ceil((faltaParaMeta / s.abonos.total) * 100)}% en todos los abonos,
              o a sumar {Math.ceil(faltaParaMeta / (s.abonos.total / s.abonos.cant))} abonos del tamaño promedio.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
